import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';

import type { TableVM } from 'ab-viewModels/account/table.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatPhoneNumber } from '@acceligentllc/shared/utils/phone';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { AccountPermissionTemplateLabel } from '@acceligentllc/shared/enums/accountPermissionTemplateOptions';
import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { MemberInvite } from '@acceligentllc/shared/enums/memberInvite';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { UserPermission } from '@acceligentllc/shared/enums/userPermission';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import { DEFAULT_EMPLOYEE_IMAGE, FAKE_ACCOUNT_DATA } from 'ab-common/constants/value';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ImageTag from 'af-components/Image';
import Table from 'af-components/Table6';
import type { TabProps, Column, ButtonData, ItemBlueprint, LegacyTableRef } from 'af-components/Table6';
import DateCell from 'af-components/Table6/Cells/DateCell';
import EmptyCell from 'af-components/Table6/Cells/EmptyCell';
import StatusCell from 'af-components/Table6/Cells/StatusCell';
import type TableComponent from 'af-components/Table6/Table';

import * as AccountActions from 'af-actions/accounts';

type OwnProps = CustomRouteComponentProps;

type Props = OwnProps & ConnectedProps<typeof connector>;

const IMAGE_COLUMN_WIDTH = 100;

const CompanyMembersList: React.FC<Props> = (props: Props) => {
	const {
		deleteAccount,
		hasManagePermission,
		location: { state: { orgAlias }, pathname },
		companyName,
		history,
		activateAccount,
		resendEmailInvite,
		resendPhoneInvite,
		fetchActive,
		fetchDeleted,
		findForCSV,
		canBulkImportUsers,
	} = props;
	const tableRef = React.useRef<TableComponent<TableVM>>(null);

	const [filterText, setFilterText] = React.useState<string>('');

	const SHARED_COLUMNS: Column<TableVM>[] = React.useMemo(() => ([
		{
			accessor: 'user.imageUrl',
			sortable: false,
			width: IMAGE_COLUMN_WIDTH,
			className: 'rt-thumbnail',
			Cell: ({ original }) => (
				<ImageTag
					fallbackSrc={DEFAULT_EMPLOYEE_IMAGE}
					minSize={BlobStorageImageSizeContainer.SIZE_50X50}
					src={original.imageUrl}
					tryOriginal={true}
					tryRoot={true}
				/>
			),
		},
		{
			Header: 'Unique ID',
			accessor: 'uniqueId',
			className: 'text-strong',
			Cell: ({ original }) => original.uniqueId,
		},
		{
			Header: 'Full Name',
			accessor: 'fullName',
			className: 'text-strong',
			Cell: ({ original }) => original.fullName,
		},
		{
			Header: 'Email Address',
			accessor: 'email',
			Cell: ({ original }) => original.email ?? <EmptyCell />,
		},
		{
			Header: 'Mobile Phone',
			accessor: 'phoneNumber',
			Cell: ({ original }) => original.phoneNumber
				? formatPhoneNumber(original.phoneNumber, original.countryCode)
				: <EmptyCell />,
		},
		{
			Header: 'User Role',
			accessor: 'accountTemplate',
			Cell: ({ original }) => <span>{AccountPermissionTemplateLabel[original.accountTemplate]}</span>,
		},
		{
			Header: 'Home Office',
			accessor: 'location.nickname',
			Cell: ({ original }) => original.location ?? <EmptyCell />,
		},
		{
			Header: 'Last Login',
			accessor: 'lastLoginAt',
			Cell: ({ original }) => original.lastLoginAt
				? <DateCell date={original.lastLoginAt} format={TimeFormat.DATE_ONLY} isLeftAligned={true} withTime={true} />
				: <EmptyCell />,
		},
	]), []);

	const COLUMN_LMS: Column<TableVM> = React.useMemo(() => ({
		Header: 'LMS access',
		accessor: 'activeInLMS',
		filterable: false,
		Cell: ({ original }) => {
			if (original.activeInLMS) {
				return (<span className="text-green">Yes</span>);
			}
			return (<span className="text-grey">No</span>);
		},
	}), []);

	const COLUMNS_FOR_DELETED: Column<TableVM>[] = React.useMemo(() => [
		...SHARED_COLUMNS,
		COLUMN_LMS,
	], [COLUMN_LMS, SHARED_COLUMNS]);

	const COLUMNS_FOR_ACTIVE: Column<TableVM>[] = React.useMemo(() => [
		...SHARED_COLUMNS,
		{
			Header: 'Status',
			accessor: 'status',
			filterable: false,
			Cell: ({ original }) => <StatusCell {...original} />,
		},
		{
			Header: 'SMS Subscribed Status',
			accessor: 'subscriptionStatus',
			sortable: false,
			Cell: ({ original }) => original.subscriptionStatus,
		},
		COLUMN_LMS,
	], [COLUMN_LMS, SHARED_COLUMNS]);

	const deleteAccountModalTitle = (original: TableVM) => `Are you sure you want to delete this User for company (${original.fullName})?`;
	const deleteAccountModalBody = () => <>This action cannot be undone.</>;
	const deleteAccountModalText = () => 'Delete User';

	const handleRefreshTable = () => {
		if (tableRef?.current) {
			tableRef.current.refreshTable();
		}
	};

	const deleteAcc = React.useCallback(async (original: TableVM) => {
		await deleteAccount(original.id);
		handleRefreshTable();
	}, [deleteAccount]);

	const editAccount = React.useCallback(async (original: TableVM) => {
		history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.EDIT(orgAlias, companyName, `${original.id}`));
	}, [companyName, history, orgAlias]);

	const activateAccountModalTitle = (original: TableVM) => `Activate ${original.fullName}?`;
	const activateAccountModalBody = () => <>Activating this user will allow them to log into the platform, however, they will not be notified of this change.
		Do you want to proceed?</>;
	const activateAccountModalText = () => 'Confirm';

	const activate = React.useCallback(async (original: TableVM) => {
		await activateAccount(original.id);
	}, [activateAccount]);

	const resendEmail = React.useCallback(async (original: TableVM) => {
		await resendEmailInvite(original.id);
		history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.TABLE(orgAlias, companyName));
	}, [companyName, history, orgAlias, resendEmailInvite]);

	const resendPhone = React.useCallback(async (original: TableVM) => {
		await resendPhoneInvite(original.id);
		history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.TABLE(orgAlias, companyName));
	}, [companyName, history, orgAlias, resendPhoneInvite]);

	const goToNotificationScreen = React.useCallback(async (): Promise<void> => {
		history.push(CLIENT.COMPANY.COMMUNICATION.NOTIFY_EMPLOYEES(orgAlias, companyName, undefined, pathname));
	}, [companyName, history, orgAlias, pathname]);

	const onDownloadClick = React.useCallback(async (isDeleted: boolean, filename: string) => {
		const toastId = `settings-users-${filename}`;

		loadingToast(
			toastId,
			{ title: 'Exporting users...', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);

		const members = await findForCSV(filterText, isDeleted);
		const data = members.map((_member) => [
			_member.uniqueId,
			_member.fullName,
			_member.email,
			formatPhoneNumber(_member.phoneNumber, _member.countryCode),
			_member.status === MemberInvite.DELETED ? MemberInvite.INACTIVE : _member.status,
		]);
		data.unshift([
			'uniqueId',
			'fullName',
			'email',
			'phoneNumber',
			'status',
		]);
		downloadCSV(data, `${companyName}_${filename}.csv`);

		successToast(
			toastId,
			{ title: 'Users export complete', description: `${companyName}_${filename}.csv`, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) }
		);
	}, [companyName, filterText, findForCSV]);

	const onDownloadActiveCSVClick = React.useCallback(async () => {
		await onDownloadClick(false, 'users');
	}, [onDownloadClick]);

	const onDownloadDeactivatedCSVClick = React.useCallback(async () => {
		await onDownloadClick(true, 'deactivated_users');
	}, [onDownloadClick]);

	const onRowClick = React.useCallback(({ original }: { original: TableVM; }) => {
		history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.EDIT(orgAlias, companyName, `${original.id}`));
	}, [companyName, history, orgAlias]);

	const tabs = React.useCallback((): TabProps<TableVM>[] => {
		const activeUsersRowActions: ItemBlueprint<TableVM>[] = hasManagePermission ? [
			{
				label: 'Edit',
				action: editAccount,
				shouldRefresh: false,
			},
			{
				label: 'Activate',
				action: activate,
				hasModal: true,
				modalTitle: activateAccountModalTitle,
				modalBody: activateAccountModalBody,
				modalText: activateAccountModalText,
				shouldRefresh: true,
				hide: (original: TableVM) => original.status !== MemberInvite.INACTIVE,
			},
			{
				label: (original: TableVM) => original.status === MemberInvite.INACTIVE ? 'Send Email Invite' : 'Resend Email Invite',
				action: resendEmail,
				shouldRefresh: true,
				hide: (original: TableVM) => !original.email || original.status === MemberInvite.ACTIVE,
			},
			{
				label: (original: TableVM) => original.status === MemberInvite.INACTIVE ? 'Send Phone Invite' : 'Resend Phone Invite',
				action: resendPhone,
				shouldRefresh: true,
				hide: (original: TableVM) => !original.phoneNumber || original.status === MemberInvite.ACTIVE,
			},
			{
				label: 'Deactivate for Company',
				action: deleteAcc,
				hasModal: true,
				modalTitle: deleteAccountModalTitle,
				modalBody: deleteAccountModalBody,
				modalText: deleteAccountModalText,
				shouldRefresh: true,
			},
		] : [];

		const deletedUsersRowActions: ItemBlueprint<TableVM>[] = hasManagePermission ? [
			{
				label: 'Reactivate User',
				action: editAccount,
				shouldRefresh: false,
			},
		] : [];

		const sharedButtons: ButtonData[] = [
			{
				type: TableButtonType.LINK,
				hasPermission: hasManagePermission,
				label: 'Notify Users',
				icon: 'NotificationBellActive',
				onClick: goToNotificationScreen,
			},
			{
				type: TableButtonType.PRIMARY,
				hasPermission: hasManagePermission,
				label: 'Register User',
				onClick: async () => history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.INVITE(orgAlias, companyName)),
			},
		];

		if (canBulkImportUsers) {
			sharedButtons.unshift(
				{
					type: TableButtonType.LINK,
					hasPermission: hasManagePermission,
					label: 'CSV Import',
					icon: 'ActionImport',
					onClick: async () => history.push(CLIENT.COMPANY.SETTINGS.MEMBERS.BULK_UPLOAD(orgAlias, companyName)),
				}
			);
		}

		const spliceIndex = canBulkImportUsers ? 2 : 1;

		const activeUserButtons: ButtonData[] = sharedButtons.toSpliced(spliceIndex, 0,
			{
				type: TableButtonType.EXPORT,
				hasPermission: hasManagePermission,
				onClick: onDownloadActiveCSVClick,
			}
		);

		const deletedUserButtons: ButtonData[] = sharedButtons.toSpliced(spliceIndex, 0,
			{
				type: TableButtonType.EXPORT,
				hasPermission: hasManagePermission,
				onClick: onDownloadDeactivatedCSVClick,
			}
		);

		return [
			{
				label: 'All',
				columns: COLUMNS_FOR_ACTIVE,
				selectable: false,
				hasSearchInput: true,
				searchLabel: 'Users',
				buttons: activeUserButtons,
				fetch: fetchActive,
				onRowClick: onRowClick,
				rowActions: activeUsersRowActions,
			},
			{
				label: 'Recently Deactivated',
				columns: COLUMNS_FOR_DELETED,
				selectable: false,
				hasSearchInput: true,
				searchLabel: 'Users',
				buttons: deletedUserButtons,
				fetch: fetchDeleted,
				rowActions: deletedUsersRowActions,
			},
		];
	}, [
		COLUMNS_FOR_ACTIVE,
		COLUMNS_FOR_DELETED,
		activate,
		companyName,
		deleteAcc,
		editAccount,
		fetchActive,
		fetchDeleted,
		goToNotificationScreen,
		hasManagePermission,
		history,
		onDownloadActiveCSVClick,
		onDownloadDeactivatedCSVClick,
		onRowClick,
		orgAlias,
		resendEmail,
		resendPhone,
		canBulkImportUsers,
	]);

	return (
		<div className="form-segment form-segment--maxi">
			<Breadcrumbs items={[{ label: 'Users' }]} />
			<Table
				onFilterTextChange={setFilterText}
				ref={tableRef as unknown as LegacyTableRef<TableVM>}
				tableName={TableNameEnum.USER}
				tabs={tabs()}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { userData, companyData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasManagePermission: boolean = isAllowed(
		PagePermissions.COMPANY.SETTINGS.MEMBERS.MANAGE,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const canBulkImportUsers =
		companyData.isCompanyAdmin
		|| userData.id === FAKE_ACCOUNT_DATA.USER_ID
		|| userData.role === UserPermission.ADMIN
		|| userData.role === UserPermission.OWNER;

	return {
		hasManagePermission,
		companyName: companyData.name,
		canBulkImportUsers,
	};
}

function mapDispatchToProps() {
	return {
		deleteAccount: AccountActions.remove,
		activateAccount: AccountActions.activate,
		fetchActive: AccountActions.findAllForCompanyTable,
		fetchDeleted: AccountActions.findAllDeletedForCompanyTable,
		resendEmailInvite: AccountActions.resendEmailInvite,
		resendPhoneInvite: AccountActions.resendPhoneInvite,
		findForCSV: AccountActions.findAllForCompanyList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(CompanyMembersList);
